import type { Aircraft, AircraftPhoto, BBox, FlightPlanResponse, FlightTraceResponse, StatesResponse } from '@flightscanner/shared';

import type { SatellitesResponse, VesselsResponse } from './types';

const FETCH_TIMEOUT_MS = 30_000;
const CONFIGURED_BASE = normalizeBase(import.meta.env.VITE_API_BASE);
/** Same Worker the Pages app is deployed against. Used when VITE_API_BASE was not baked in. */
const PRODUCTION_WORKER = 'https://flightscanner-api.fruir.workers.dev';

class ApiStatusError extends Error {
  constructor(
    readonly path: string,
    readonly status: number,
    readonly base: string,
  ) {
    super(`API ${path} -> ${status}`);
  }
}

function normalizeBase(base: string | undefined): string {
  return (base ?? '').trim().replace(/\/+$/, '');
}

function isLocalWebOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function apiBases(): string[] {
  const bases = [CONFIGURED_BASE, ''];
  if (isLocalWebOrigin()) bases.push('http://127.0.0.1:8787', 'http://localhost:8787');
  else if (!CONFIGURED_BASE) bases.push(PRODUCTION_WORKER);
  return [...new Set(bases)];
}

function apiUrl(base: string, path: string): string {
  return base ? `${base}${path}` : path;
}

function shouldTryNextBase(err: unknown): boolean {
  if ((err as Error).name === 'AbortError') return false;
  if (err instanceof ApiStatusError) return err.status === 404 || err.status >= 500;
  const msg = String(err);
  return msg.includes('Failed to fetch') || msg.includes('NetworkError');
}

async function getJson<T>(path: string, timeoutMs = FETCH_TIMEOUT_MS, signal?: AbortSignal): Promise<T> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  let lastErr: unknown = null;
  try {
    for (const base of apiBases()) {
      try {
        const res = await fetch(apiUrl(base, path), { signal: ctrl.signal });
        if (!res.ok) throw new ApiStatusError(path, res.status, base || 'same-origin');
        return (await res.json()) as T;
      } catch (err) {
        lastErr = err;
        if (!shouldTryNextBase(err)) throw err;
      }
    }
    throw lastErr;
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      if (signal?.aborted) throw err;
      throw new Error(`API ${path} timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(id);
  }
}

export async function fetchStates(
  bbox: BBox,
  zoom: number,
  opts: { globalFilter?: 'mil' | null; signal?: AbortSignal } = {},
): Promise<StatesResponse> {
  const p = new URLSearchParams({
    bbox: `${bbox.minLon},${bbox.minLat},${bbox.maxLon},${bbox.maxLat}`,
    zoom: String(Math.round(zoom)),
  });
  if (opts.globalFilter) p.set('globalFilter', opts.globalFilter);
  return getJson<StatesResponse>(`/api/states?${p}`, FETCH_TIMEOUT_MS, opts.signal);
}

export function fetchAircraft(icao: string, signal?: AbortSignal): Promise<Aircraft> {
  return getJson<Aircraft>(`/api/aircraft/${icao}`, FETCH_TIMEOUT_MS, signal);
}

export function fetchTrace(icao: string, signal?: AbortSignal): Promise<FlightTraceResponse> {
  return getJson<FlightTraceResponse>(`/api/trace/${icao}`, FETCH_TIMEOUT_MS, signal);
}

export function fetchPhoto(
  icao: string,
  opts: { type?: string | null; reg?: string | null; description?: string | null; signal?: AbortSignal } = {},
): Promise<AircraftPhoto> {
  const p = new URLSearchParams();
  if (opts.type) p.set('type', opts.type);
  if (opts.reg) p.set('reg', opts.reg);
  if (opts.description) p.set('desc', opts.description);
  const qs = p.toString();
  return getJson<AircraftPhoto>(`/api/photo/${icao}${qs ? `?${qs}` : ''}`, FETCH_TIMEOUT_MS, opts.signal);
}

export async function fetchVessels(bbox: BBox, signal?: AbortSignal): Promise<VesselsResponse> {
  const p = new URLSearchParams({
    bbox: `${bbox.minLon},${bbox.minLat},${bbox.maxLon},${bbox.maxLat}`,
  });
  return getJson<VesselsResponse>(`/api/vessels?${p}`, FETCH_TIMEOUT_MS, signal);
}

export async function fetchSatellites(
  group: string,
  signal?: AbortSignal,
): Promise<SatellitesResponse> {
  const p = new URLSearchParams({ group });
  return getJson<SatellitesResponse>(`/api/satellites?${p}`, FETCH_TIMEOUT_MS, signal);
}

export function fetchFlightPlan(
  icao: string,
  opts: {
    callsign?: string | null;
    lat?: number | null;
    lon?: number | null;
    military?: boolean;
    operatorCountry?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<FlightPlanResponse> {
  const p = new URLSearchParams();
  if (opts.callsign) p.set('callsign', opts.callsign);
  if (opts.lat != null && Number.isFinite(opts.lat)) p.set('lat', String(opts.lat));
  if (opts.lon != null && Number.isFinite(opts.lon)) p.set('lon', String(opts.lon));
  if (opts.military) p.set('military', '1');
  if (opts.operatorCountry) p.set('operatorCountry', opts.operatorCountry);
  const qs = p.toString();
  return getJson<FlightPlanResponse>(`/api/flight/${icao}${qs ? `?${qs}` : ''}`, FETCH_TIMEOUT_MS, opts.signal);
}
